import { sendEpgRecord as sendEpgRecordAction } from 'common/actions';
import variables from 'common/config/variables';
import { Epg } from 'common/constants/data-types';
import { StoreState } from 'index';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

function usePlayerRecords(epg?: Epg) {
  const lastWatched = useSelector(
    (state: StoreState) => state.lastWatched as Epg[] | undefined,
    shallowEqual
  );
  const shouldSendPlayerTimestampRef = useRef<boolean>(false);
  const sendPlayerEventsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRecordSentRef = useRef<number>(0);
  const isInWatchHistoryRef = useRef<boolean>(false);
  const currentEpgRef = useRef<Epg | undefined>(epg);
  const isPlayerTimestampSendEnabled = useRef<boolean>(false);

  useEffect(() => {
    sendPlayerEventsTimeoutRef.current && clearTimeout(sendPlayerEventsTimeoutRef.current);
    sendPlayerEventsTimeoutRef.current = null;
    shouldSendPlayerTimestampRef.current = false;
    lastRecordSentRef.current = Date.now();
    currentEpgRef.current = epg;
    isPlayerTimestampSendEnabled.current = false;
  }, [epg]);

  useEffect(() => {
    isInWatchHistoryRef.current = !!lastWatched?.find((e) => {
      return e.eventid === epg?.eventid || e.contentId === epg?.contentId;
    });
  }, [epg, lastWatched]);

  const sendPlayerProgress = useCallback(async (currentTime: number) => {
    if (isEmpty(currentEpgRef.current)) {
      console.warn('No EPG data available for sending player records.');
      return;
    }
    const now = Date.now();
    const deltaTime = Math.abs(now - lastRecordSentRef.current);
    const shouldSendByTime = deltaTime > variables.PLAYER_RECORDS_UPDATE_INTERVAL;
    // Immediately start sending timestamps if the content is in watch history
    if (shouldSendByTime || isInWatchHistoryRef.current || isPlayerTimestampSendEnabled.current) {
      if (!isPlayerTimestampSendEnabled.current)
        isPlayerTimestampSendEnabled.current = shouldSendByTime;

      const response = (await sendEpgRecordAction({
        contentId: currentEpgRef.current.contentId,
        eventId: currentEpgRef.current.eventid,
        offset: Math.floor(currentTime)
      })) as unknown as boolean;
      if (response) lastRecordSentRef.current = now;
    }
  }, []);

  return { sendPlayerProgress };
}

export default usePlayerRecords;
